import { Contract, SorobanDataBuilder } from "../base/index.js";
import { Api } from "./api.js";
import {
  ContractEvent,
  DiagnosticEvent,
  LedgerCloseMeta,
  LedgerEntry,
  LedgerEntryData,
  LedgerHeader,
  LedgerHeaderHistoryEntry,
  LedgerKey,
  ScVal,
  SorobanAuthorizationEntry,
  TransactionEnvelope,
  TransactionEvent,
  TransactionMeta,
  TransactionMetaV3,
  TransactionMetaV4,
  TransactionResult,
} from "../xdr/index.js";

/**
 * Parse the response from invoking the `submitTransaction` method of a RPC server.
 * @hidden
 *
 * @param raw - the raw `submitTransaction` response from the RPC server to parse
 * @returns transaction response parsed from the RPC server's response
 */
export function parseRawSendTransaction(
  raw: Api.RawSendTransactionResponse,
): Api.SendTransactionResponse {
  const { errorResultXdr, diagnosticEventsXdr } = raw;
  delete raw.errorResultXdr;
  delete raw.diagnosticEventsXdr;

  if (errorResultXdr) {
    return {
      ...raw,
      ...(diagnosticEventsXdr !== undefined &&
        diagnosticEventsXdr.length > 0 && {
          diagnosticEvents: diagnosticEventsXdr.map((evt) =>
            DiagnosticEvent.fromXdr(evt, "base64"),
          ),
        }),
      errorResult: TransactionResult.fromXdr(errorResultXdr, "base64"),
    };
  }

  return { ...raw } as Api.BaseSendTransactionResponse;
}

export function parseTransactionInfo(
  raw: Api.RawTransactionInfo | Api.RawGetTransactionResponse,
): Omit<Api.TransactionInfo, "status" | "txHash"> {
  const meta = TransactionMeta.fromXdr(raw.resultMetaXdr!, "base64");
  const info: Omit<Api.TransactionInfo, "status" | "txHash"> = {
    ledger: raw.ledger!,
    createdAt: raw.createdAt!,
    applicationOrder: raw.applicationOrder!,
    feeBump: raw.feeBump!,
    envelopeXdr: TransactionEnvelope.fromXdr(raw.envelopeXdr!, "base64"),
    resultXdr: TransactionResult.fromXdr(raw.resultXdr!, "base64"),
    resultMetaXdr: meta,
    events: {
      contractEventsXdr: (raw.events?.contractEventsXdr ?? []).map((lst) =>
        lst.map((e) => ContractEvent.fromXdr(e, "base64")),
      ),
      transactionEventsXdr: (raw.events?.transactionEventsXdr ?? []).map((e) =>
        TransactionEvent.fromXdr(e, "base64"),
      ),
    },
  };

  switch (meta.type) {
    case "v3":
    case "v4": {
      const metaV = meta.value as TransactionMetaV3 | TransactionMetaV4;
      if (metaV.sorobanMeta !== null) {
        info.returnValue = metaV.sorobanMeta.returnValue ?? undefined;
      }
    }
  }

  if (raw.diagnosticEventsXdr) {
    info.diagnosticEventsXdr = raw.diagnosticEventsXdr.map((e) =>
      DiagnosticEvent.fromXdr(e, "base64"),
    );
  }

  return info;
}

export function parseRawTransactions(
  r: Api.RawTransactionInfo,
): Api.TransactionInfo {
  return {
    status: r.status,
    txHash: r.txHash,
    ...parseTransactionInfo(r),
  };
}

/**
 * Parse and return the retrieved events, if any, from a raw response from a
 * RPC server.
 *
 * @param raw - the raw `getEvents` response from the
 *    RPC server to parse
 * @returns events parsed from the RPC server's
 *    response
 */
export function parseRawEvents(
  raw: Api.RawGetEventsResponse,
): Api.GetEventsResponse {
  return {
    latestLedger: raw.latestLedger,
    oldestLedger: raw.oldestLedger,
    latestLedgerCloseTime: raw.latestLedgerCloseTime,
    oldestLedgerCloseTime: raw.oldestLedgerCloseTime,

    cursor: raw.cursor,
    events: (raw.events ?? []).map((evt) => {
      const clone: Omit<Api.RawEventResponse, "contractId"> = { ...evt };
      delete (clone as any).contractId; // `as any` hack because contractId field isn't optional

      // the contractId may be empty so we omit the field in that case
      return {
        ...clone,
        ...(evt.contractId !== "" && {
          contractId: new Contract(evt.contractId),
        }),
        topic: (evt.topic ?? []).map((topic) => ScVal.fromXdr(topic, "base64")),
        value: ScVal.fromXdr(evt.value, "base64"),
      };
    }),
  };
}

/**
 * Parse and return the retrieved ledger entries, if any, from a raw response
 * from a RPC server.
 * @hidden
 *
 * @param raw - the raw `getLedgerEntries`
 *    response from the RPC server to parse
 * @returns ledger entries parsed from the
 *    RPC server's response
 */
export function parseRawLedgerEntries(
  raw: Api.RawGetLedgerEntriesResponse,
): Api.GetLedgerEntriesResponse {
  return {
    latestLedger: raw.latestLedger,
    entries: (raw.entries ?? []).map((rawEntry) => {
      if (!rawEntry.key || !rawEntry.xdr) {
        throw new TypeError(
          `invalid ledger entry: ${JSON.stringify(rawEntry)}`,
        );
      }

      return {
        lastModifiedLedgerSeq: rawEntry.lastModifiedLedgerSeq,
        key: LedgerKey.fromXdr(rawEntry.key, "base64"),
        val: LedgerEntryData.fromXdr(rawEntry.xdr, "base64"),
        ...(rawEntry.liveUntilLedgerSeq !== undefined && {
          liveUntilLedgerSeq: rawEntry.liveUntilLedgerSeq,
        }),
      };
    }),
  };
}

/**
 * Parse whether or not the transaction simulation was successful, returning the
 * relevant response.
 * @hidden
 *
 * @param sim - a raw response from the
 *    `simulateTransaction` method of the RPC server to parse
 * @param partial - a partially built
 *    simulate transaction response that will be used to build the return
 *    response
 * @returns Either a simulation response indicating what ledger entries
 *    should be restored, or if the simulation was successful.
 */
function parseSuccessful(
  sim: Api.RawSimulateTransactionResponse,
  partial: Api.BaseSimulateTransactionResponse,
):
  | Api.SimulateTransactionRestoreResponse
  | Api.SimulateTransactionSuccessResponse {
  // success type: might have a result (if invoking) and...
  const success: Api.SimulateTransactionSuccessResponse = {
    ...partial,
    transactionData: new SorobanDataBuilder(sim.transactionData!),
    minResourceFee: sim.minResourceFee!,
    // coalesce 0-or-1-element results[] list into a single result struct
    // with decoded fields if present
    ...((sim.results?.length ?? 0) > 0 && {
      result: sim.results!.map((row) => ({
        auth: (row.auth ?? []).map((entry) =>
          SorobanAuthorizationEntry.fromXdr(entry, "base64"),
        ),
        // if return value is missing ("falsy") we coalesce to void
        retval: row.xdr ? ScVal.fromXdr(row.xdr, "base64") : ScVal.scvVoid(),
      }))[0],
    }),

    ...((sim.stateChanges?.length ?? 0) > 0 && {
      stateChanges: sim.stateChanges?.map((entryChange) => ({
        type: entryChange.type,
        key: LedgerKey.fromXdr(entryChange.key, "base64"),
        before: entryChange.before
          ? LedgerEntry.fromXdr(entryChange.before, "base64")
          : null,
        after: entryChange.after
          ? LedgerEntry.fromXdr(entryChange.after, "base64")
          : null,
      })),
    }),
  };

  if (!sim.restorePreamble || sim.restorePreamble.transactionData === "") {
    return success;
  }

  // ...might have a restoration hint (if some state is expired)
  return {
    ...success,
    restorePreamble: {
      minResourceFee: sim.restorePreamble!.minResourceFee,
      transactionData: new SorobanDataBuilder(
        sim.restorePreamble!.transactionData,
      ),
    },
  };
}

/**
 * Converts a raw response schema into one with parsed XDR fields and a simplified interface.
 *
 * **Warning:** This API is only exported for testing purposes and should not be relied on or considered "stable".
 *
 * @param sim - the raw response schema (parsed ones are allowed, best-effort
 *    detected, and returned untouched)
 * @returns the original parameter (if already parsed), parsed otherwise
 */
export function parseRawSimulation(
  sim: Api.SimulateTransactionResponse | Api.RawSimulateTransactionResponse,
): Api.SimulateTransactionResponse {
  const looksRaw = Api.isSimulationRaw(sim);
  if (!looksRaw) {
    // Gordon Ramsey in shambles
    return sim;
  }

  // shared across all responses
  const base: Api.BaseSimulateTransactionResponse = {
    _parsed: true,
    id: sim.id,
    latestLedger: sim.latestLedger,
    events:
      sim.events?.map((evt) => DiagnosticEvent.fromXdr(evt, "base64")) ?? [],
  };

  // error type: just has error string
  if (typeof sim.error === "string") {
    return {
      ...base,
      error: sim.error,
    };
  }

  return parseSuccessful(sim, base);
}

export function parseRawLedger(raw: Api.RawLedgerResponse): Api.LedgerResponse {
  if (!raw.metadataXdr || !raw.headerXdr) {
    let missingFields: string;
    if (!raw.metadataXdr && !raw.headerXdr) {
      missingFields = "metadataXdr and headerXdr";
    } else if (!raw.metadataXdr) {
      missingFields = "metadataXdr";
    } else {
      missingFields = "headerXdr";
    }
    throw new TypeError(`invalid ledger missing fields: ${missingFields}`);
  }

  const metadataXdr = LedgerCloseMeta.fromXdr(raw.metadataXdr, "base64");
  const headerXdr = LedgerHeaderHistoryEntry.fromXdr(raw.headerXdr, "base64");
  return {
    hash: raw.hash,
    sequence: raw.sequence,
    ledgerCloseTime: raw.ledgerCloseTime,
    metadataXdr,
    headerXdr,
  };
}

export function parseRawLatestLedger(
  raw: Api.RawGetLatestLedgerResponse,
): Api.GetLatestLedgerResponse {
  const headerXdr = LedgerHeader.fromXdr(raw.headerXdr, "base64");
  const metadataXdr = LedgerCloseMeta.fromXdr(raw.metadataXdr, "base64");
  let missingFields;
  if (!raw.metadataXdr && !raw.headerXdr) {
    missingFields = "metadataXdr and headerXdr";
  } else if (!raw.metadataXdr) {
    missingFields = "metadataXdr";
  } else if (!raw.headerXdr) {
    missingFields = "headerXdr";
  }
  if (missingFields) {
    throw new TypeError(
      `invalid getLatestLedger response missing fields: ${missingFields}`,
    );
  }

  return {
    id: raw.id,
    sequence: raw.sequence,
    protocolVersion: raw.protocolVersion,
    closeTime: raw.closeTime,
    headerXdr,
    metadataXdr,
  };
}
